import { useParams, Link } from 'react-router';
import { useAuthStore } from '../store/auth';
import { useRegistrations } from '../hooks/useRegistrations';
import { Button, LoadingSpinner, Card } from '@lms/ui';
import { Printer, ChevronLeft } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
import { Bilingual } from '../components/Bilingual';
import { copy } from '../copy/copy';

export function Receipt() {
  const { t } = useLanguage();
  const { id } = useParams<{ id: string }>(); // This could be the registration ID
  const { user } = useAuthStore();
  const { data: registrationsResponse, isLoading } = useRegistrations(user?.id);

  // `getByUser` returns { items, meta } — unwrap to find the row whose
  // id matches the `:id` route param.
  const registration = registrationsResponse?.items.find(r => r.id === id);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="lg" className="text-primary" />
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="text-center py-12">
        <p className="text-error font-body">
          <Bilingual {...copy.receipt.notFound} />
        </p>
        <Link
          to="/"
          className="text-primary hover:underline font-label uppercase text-sm font-semibold mt-4 inline-block"
        >
          <Bilingual {...copy.receipt.backToDashboard} />
        </Link>
      </div>
    );
  }

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8 animate-fadeIn pb-12">
      {/* Non-printable header */}
      <div className="flex justify-between items-center print:hidden px-2">
        <Link
          to="/"
          className="text-on-surface-variant hover:text-primary flex items-center gap-2 font-label uppercase text-[10px] font-bold tracking-widest no-underline transition-colors"
        >
          <ChevronLeft size={16} /> <Bilingual {...copy.shell.dashboard} />
        </Link>
        <Button
          variant="outline"
          size="sm"
          onClick={handlePrint}
          className="gap-2 rounded-xl text-[10px] uppercase font-bold tracking-widest"
        >
          <Printer size={16} /> <Bilingual {...copy.receipt.printArchive} />
        </Button>
      </div>

      {/* Printable Receipt Canvas (Paper Aesthetic) */}
      <Card
        variant="elevated"
        className="bg-surface-container-lowest print:shadow-none print:bg-white !p-12 relative overflow-hidden"
      >
        <div className="absolute top-0 left-0 w-full h-1.5 bg-primary opacity-20" />

        <div className="text-center mb-12 border-b border-outline-variant/10 pb-8">
          <h1 className="text-3xl font-headline font-black text-on-surface uppercase tracking-[0.25em] m-0">
            <Bilingual {...copy.receipt.dossierProof} />
          </h1>
          <p className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest mt-3 opacity-60">
            <Bilingual {...copy.receipt.digitalReceiptSub} />
          </p>
        </div>

        <div className="space-y-10 font-body">
          <div className="grid grid-cols-3 gap-6 items-baseline">
            <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-40">
              <Bilingual {...copy.receipt.identifier} />
            </div>
            <div className="col-span-2 font-mono font-bold text-primary text-base">
              {registration.participantId?.participantCode ||
                `#REG-${registration.id.slice(-8).toUpperCase()}`}
            </div>
          </div>

          {[
            { label: t(copy.receipt.scholarIdentity), value: user?.name },
            {
              label: t(copy.receipt.scholarIdentifier),
              value:
                registration.participantId?.participantCode || t(copy.receipt.pendingAuthorization),
            },
            { label: t(copy.receipt.communication), value: user?.email },
            { label: t(copy.receipt.academicTrack), value: registration.program?.name, bold: true },
            {
              label: t(copy.receipt.calibrationDate),
              value: new Date(registration.registeredAt).toLocaleDateString(
                t({ id: 'id-ID', ar: 'ar-SA' }),
                {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }
              ),
            },
            {
              label: t(copy.receipt.currentStatus),
              value: t(
                copy.common.registrationStatus[
                  registration.status as keyof typeof copy.common.registrationStatus
                ] || { id: registration.status, ar: registration.status }
              ),
              uppercase: true,
              color: 'text-primary',
            },
            {
              label: t(copy.receipt.adminSupport),
              value: 'WhatsApp: +62 812-2222-2222 (TSL Helpdesk)',
              color: 'text-on-surface-variant',
            },
          ].map(item => (
            <div key={item.label} className="grid grid-cols-3 gap-6 items-baseline">
              <div className="text-[10px] font-label font-bold text-on-surface-variant uppercase tracking-widest opacity-40">
                {item.label}
              </div>
              <div
                className={`col-span-2 text-base font-body ${item.bold ? 'font-bold text-on-surface' : 'text-on-surface-variant'} ${item.uppercase ? 'uppercase font-black text-xs tracking-widest' : ''} ${item.color || ''}`}
              >
                {item.value || t(copy.receipt.pendingSync)}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 pt-8 border-t border-outline-variant/10 text-[9px] font-label font-bold text-center text-on-surface-variant uppercase tracking-widest opacity-40 space-y-1">
          <p>
            <Bilingual {...copy.receipt.certProof} />
          </p>
          <p>
            <Bilingual {...copy.receipt.secureTrans} />
          </p>
        </div>
      </Card>
    </div>
  );
}
